import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Plus, X, Loader2, Sparkles } from "lucide-react";
import ArtifactQualityReview from "../../ArtifactQualityReview";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ControlPlanTab({ projectId, token, controlPlan, setControlPlan }) {
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const addItem = () => {
    setControlPlan(prev => ({
      ...prev,
      items: [...prev.items, { process_step: "", characteristic: "", specification: "", measurement_method: "", sample_size: "", frequency: "", control_method: "", reaction_plan: "", responsible: "" }]
    }));
  };

  const updateItem = (index, field, value) => {
    setControlPlan(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (index) => {
    if (controlPlan.items.length <= 1) return;
    setControlPlan(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const response = await fetch(`${API}/ai/suggest-control-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!response.ok) throw new Error("Failed to generate control plan");
      const data = await response.json();
      if (data.items?.length) {
        setControlPlan(prev => ({ ...prev, items: data.items }));
        toast.success(`AI generated ${data.items.length} control plan items`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSuggesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/control/control-plan`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          items: controlPlan.items.filter(i => i.process_step.trim()),
          revision: controlPlan.revision,
          approved_by: controlPlan.approved_by,
        }),
      });
      if (!response.ok) throw new Error("Failed to save");
      toast.success("Control plan saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold">Control Plan</h3>
          <p className="text-sm text-muted-foreground">Define how to sustain improvements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={aiSuggest} disabled={suggesting} className="gap-1" data-testid="ai-suggest-control-plan-btn">
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {suggesting ? "Generating..." : "AI Suggest"}
            <CreditCostTag cost={12} />
          </Button>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="add-control-plan-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label>Revision</Label>
          <Input type="number" value={controlPlan.revision} onChange={(e) => setControlPlan(prev => ({ ...prev, revision: parseInt(e.target.value) || 1 }))} className="w-24" data-testid="cp-revision" />
        </div>
        <div className="space-y-2">
          <Label>Approved By</Label>
          <Input value={controlPlan.approved_by} onChange={(e) => setControlPlan(prev => ({ ...prev, approved_by: e.target.value }))} placeholder="Champion/Sponsor name" data-testid="cp-approved-by" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Process Step</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Characteristic</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Specification</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Measurement</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Frequency</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Control Method</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Reaction Plan</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {controlPlan.items.map((item, index) => (
              <tr key={index} className="border-b border-border">
                <td className="py-2 px-2">
                  <Input value={item.process_step} onChange={(e) => updateItem(index, "process_step", e.target.value)} placeholder="Step" className="text-sm" data-testid={`cp-step-${index}`} />
                </td>
                <td className="py-2 px-2">
                  <Input value={item.characteristic} onChange={(e) => updateItem(index, "characteristic", e.target.value)} placeholder="What to control" className="text-sm" data-testid={`cp-char-${index}`} />
                </td>
                <td className="py-2 px-2">
                  <Input value={item.specification} onChange={(e) => updateItem(index, "specification", e.target.value)} placeholder="Spec/Target" className="text-sm" data-testid={`cp-spec-${index}`} />
                </td>
                <td className="py-2 px-2">
                  <Input value={item.measurement_method} onChange={(e) => updateItem(index, "measurement_method", e.target.value)} placeholder="How to measure" className="text-sm" data-testid={`cp-measurement-${index}`} />
                </td>
                <td className="py-2 px-2">
                  <select value={item.frequency} onChange={(e) => updateItem(index, "frequency", e.target.value)} className="w-full h-10 px-2 rounded-md border border-input bg-background text-sm" data-testid={`cp-freq-${index}`}>
                    <option value="continuous">Continuous</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </td>
                <td className="py-2 px-2">
                  <Input value={item.control_method} onChange={(e) => updateItem(index, "control_method", e.target.value)} placeholder="Control chart, checklist..." className="text-sm" data-testid={`cp-control-${index}`} />
                </td>
                <td className="py-2 px-2">
                  <Input value={item.reaction_plan} onChange={(e) => updateItem(index, "reaction_plan", e.target.value)} placeholder="If out of spec..." className="text-sm" data-testid={`cp-reaction-${index}`} />
                </td>
                <td className="py-2 px-2">
                  {controlPlan.items.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeItem(index)}><X className="w-3 h-3" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-control-plan-btn">
        {saving ? "Saving..." : "Save Control Plan"}
      </Button>

      {controlPlan.items.some(item => item.process_step) && (
        <ArtifactQualityReview projectId={projectId} artifactType="control_plan" token={token} className="mt-6" />
      )}
    </div>
  );
}
